import {
  Button,
  Flex,
  IconButton,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import InputNumber from '../../../components/shared/Inputs/InputNumber';
import InputText from '../../../components/shared/Inputs/InputText';
import { useAuth } from '../../../hooks/auth';
import backend from '../../../services/backend';
import { useUsuarios } from '../../../services/hooks/useUsuarios';
import { queryClient } from '../../../services/queryClient';
import { requestHelper } from '../../../utils/requestHelper';

export const FormSchema = z.object({
  comissao_pai: z.number({ required_error: 'Obrigatório' }),
});

interface IAceitaUsuarioModal {
  close?: any;
  open: boolean;
  id_usuario: number;
}

const AceitaUsuarioModal: React.FC<IAceitaUsuarioModal> = ({
  close,
  open,
  id_usuario,
}) => {
  // ESTILOS
  const color = useColorModeValue('#3f3f46', '#fff');
  const bg = useColorModeValue('white', 'gray.800');
  const toast = useToast();

  // DADOS
  const history = useHistory();
  const { data, isLoading, refetch } = useUsuarios('', '1');
  const parceirosData = data?.rows ?? [];
  const { user } = useAuth();
  const [dadosUsuarioPai, setDadosUsuarioPai] = useState<null | {
    id: number;
    nome: string;
    valor_custo_pai: number;
    valor_comissao_pai: number;
  }>(null);

  const methods = useForm({
    resolver: zodResolver(FormSchema),
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const comissao_pai = watch('comissao_pai');

  const [novoParceiroCusto, setNovoParceiroCusto] = useState(
    (comissao_pai ?? 0) + (dadosUsuarioPai?.valor_custo_pai ?? 0),
  );

  useEffect(() => {
    setNovoParceiroCusto(
      (comissao_pai ?? 0) + (dadosUsuarioPai?.valor_custo_pai ?? 0),
    );
  }, [comissao_pai, dadosUsuarioPai]);

  // ESTADOS
  const [etapaTela, setEtapaTela] = useState<
    'ESCOLHA_USUARIO_PAI' | 'ESCOLHA_CUSTO_CERTIFICADO'
  >('ESCOLHA_USUARIO_PAI');
  const [paraParceiros, setParaParceiros] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const abreEscolhaCustoCertificado = async usuarioPai => {
    await requestHelper({
      setLoading: setEnviando,
      action: () =>
        backend.get<{
          id: number;
          nome: string;
          valor_certificado: number;
        }>(`/parceiro-pai-dados/${usuarioPai}`),
      onSuccess: response => {
        setDadosUsuarioPai(response.data);
        setValue('comissao_pai', response?.data?.valor_comissao_pai);
        setEtapaTela('ESCOLHA_CUSTO_CERTIFICADO');
      },
    });
  };

  const HandleAceitaUsuario = async usuarioPai => {
    if (enviando) return;
    setEnviando(true);
    try {
      await backend.post('/aceita-parceiro', {
        novo_parceiro: id_usuario,
        parceiro_pai: usuarioPai,
        custo_certificado: novoParceiroCusto,
      });
      toast({
        title: 'Sucesso',
        description: 'atualizando usuário...',
        duration: 4000,
        status: 'success',
        position: 'bottom-right',
      });
      queryClient.setQueryData(['usuarios'], []);
      close();
      refetch();
      history.push('/usuarios');
      await queryClient.resetQueries({
        queryKey: ['usuarios'],
        refetchActive: true,
      });
    } catch (error) {
      //
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={close}>
      <ModalOverlay>
        <ModalContent borderRadius="xl">
          <ModalHeader color={color}>Aceitar usuário</ModalHeader>
          <ModalCloseButton color={color} />
          <ModalBody>
            {etapaTela === 'ESCOLHA_USUARIO_PAI' && (
              <>
                {!paraParceiros && (
                  <>
                    <Text color={color}>Aceitar parceiro como:</Text>
                    <Flex flexDir="column" justify="space-between" mt="1">
                      <Button
                        w="100"
                        onClick={() => abreEscolhaCustoCertificado(user.id)}
                        isLoading={enviando}
                      >
                        meu parceiro
                      </Button>
                      <Button
                        w="100%"
                        mt="2"
                        disabled={enviando}
                        onClick={() => setParaParceiros(true)}
                      >
                        para um dos meus parceiros
                      </Button>
                    </Flex>
                  </>
                )}
                {paraParceiros && (
                  <Flex flexDir="column">
                    <Text color={color}>Para:</Text>
                    {isLoading && (
                      <>
                        <Skeleton h="30px" w="100%" mt="2" />
                        <Skeleton h="30px" w="100%" mt="2" />
                        <Skeleton h="30px" w="100%" mt="2" />
                      </>
                    )}
                    {!isLoading && (
                      <Flex flexDir="column" maxH="500px" overflowY="auto">
                        {parceirosData?.map(item => {
                          return (
                            <Flex
                              justify="space-between"
                              mb="1"
                              borderWidth="1px"
                              align="center"
                            >
                              <Text pl="2" color={color} fontWeight="medium">
                                {item?.nome}
                              </Text>
                              <IconButton
                                aria-label="parceiro"
                                icon={<Check />}
                                rounded="0px"
                                disabled={enviando}
                                onClick={() =>
                                  abreEscolhaCustoCertificado(item.id)
                                }
                              />
                            </Flex>
                          );
                        })}
                      </Flex>
                    )}
                    {enviando && (
                      <Flex justify="center" mt="1">
                        <Spinner color="blue.500" />
                      </Flex>
                    )}
                  </Flex>
                )}
              </>
            )}
            {etapaTela === 'ESCOLHA_CUSTO_CERTIFICADO' && (
              <>
                <Text color={color}>Parceiro pai:</Text>
                <InputText value={dadosUsuarioPai?.nome} />
                <form>
                  <SimpleGrid
                    mt="3"
                    templateColumns={['1fr 1fr', '1fr 3fr', '1fr, 6fr']}
                  >
                    <InputGroup>
                      <InputLeftAddon
                        fontWeight="medium"
                        color={color}
                        roundedBottom="0px"
                      >
                        R$
                      </InputLeftAddon>
                      <Flex
                        w="100%"
                        align="center"
                        padding="7px"
                        borderWidth="1px"
                        borderTopRightRadius="xl"
                        bg="rgba(0,0,0,0.05)"
                      >
                        <Text color={color} pl="2">
                          {dadosUsuarioPai?.valor_custo_pai ?? 0}
                        </Text>
                      </Flex>
                    </InputGroup>
                    <Text color={color} pl="2" alignSelf="center">
                      Custo certificado parceiro pai
                    </Text>

                    <Controller
                      control={control}
                      name="comissao_pai"
                      render={({ field }) => {
                        return (
                          <InputGroup>
                            <InputLeftAddon
                              fontWeight="medium"
                              color={color}
                              rounded="0px"
                            >
                              R$
                            </InputLeftAddon>
                            <InputNumber
                              w="100px"
                              color={color}
                              maskType="money"
                              name={field.name}
                              defaultValue={
                                dadosUsuarioPai?.valor_comissao_pai ?? 0
                              }
                              value={field.value}
                              onChange={field.onChange}
                              error={errors.valor_venda}
                              borderRadius="0px"
                            />
                          </InputGroup>
                        );
                      }}
                    />
                    <Text color={color} pl="2" alignSelf="center">
                      Comissão parceiro pai
                    </Text>
                    <InputGroup>
                      <InputLeftAddon roundedTop="0px">
                        <Text
                          bgGradient="linear(to-b, green.300, green.500)"
                          bgClip="text"
                          fontWeight="bold"
                        >
                          R$
                        </Text>
                      </InputLeftAddon>
                      <Flex
                        align="center"
                        padding="7px"
                        borderWidth="1px"
                        w="100%"
                        borderBottomRightRadius="xl"
                        bg="rgba(0,0,0,0.05)"
                      >
                        <Text
                          fontWeight="medium"
                          bgGradient="linear(to-b, green.300, green.500)"
                          bgClip="text"
                          pl="2"
                        >
                          {novoParceiroCusto}
                        </Text>
                      </Flex>
                    </InputGroup>
                    <Text color={color} pl="2" alignSelf="center">
                      Custo do novo parceiro
                    </Text>
                  </SimpleGrid>
                </form>
                <Flex mt="2" justify="flex-end">
                  <Button
                    mr="2"
                    variant="ghost"
                    onClick={() => setEtapaTela('ESCOLHA_USUARIO_PAI')}
                  >
                    Voltar
                  </Button>
                  <Button
                    isLoading={enviando}
                    onClick={() => HandleAceitaUsuario(dadosUsuarioPai?.id)}
                  >
                    Salvar
                  </Button>
                </Flex>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

export default AceitaUsuarioModal;
